import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import {useModal} from '../../../contexts/ModalContext';
import {useAuth} from '../../../contexts/AuthContext';
import {notifyError, notifySuccess} from '../../../utils/notifications';
import {refreshInquirySummary} from '../../../utils/inquirySummary';
import RepairModal from '../../../components/RepairModal';
import {CustomerModal, ReplyModal, RescheduleModal, StatusModal, TextArea} from './InquiryModals';
import {
    apiMessage,
    CONDITION_LABELS,
    customerName,
    formatDateTime,
    formatMinutes,
    formatPrice,
    formatTerm,
    relativeDay,
    reservationDeviceName,
    reservationDevices,
    statusLabel,
    STATUSES_BY_TYPE,
    StatusBadge,
    telHref,
    TYPE_MAP,
    TypeBadge,
    userName
} from './inquiryUtils';

const SILENT = {meta: {silentSuccess: true, silentError: true}};

const InquiryDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {openModal, confirm} = useModal();
    const {user} = useAuth();
    const [inquiry, setInquiry] = useState(null);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        API.get(API_ENDPOINTS.user.list, {meta: {silentError: true}})
            .then(({data}) => setUsers((data || []).filter((item) => !item.deleted)))
            .catch(() => {});
    }, []);

    const load = useCallback(async () => {
        try {
            const {data} = await API.get(API_ENDPOINTS.webInquiry.detail(id), {meta: {silentError: true}});
            setInquiry(data);
            setError(null);
            refreshInquirySummary();
        } catch (e) {
            setError(e);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    /**
     * Zavolá akci API. Při obsazeném termínu (409) se zeptá, zda přesto pokračovat.
     * Vrací true při úspěchu (modální okno se zavře).
     */
    const run = useCallback(async (url, body, successMessage) => {
        try {
            const {data} = await API.post(url, body, SILENT);
            setInquiry(data);
            refreshInquirySummary();
            if (successMessage) notifySuccess(successMessage);
            return true;
        } catch (e) {
            if (e?.response?.status === 409 && e.response.data?.errorCode === 'slotUnavailable') {
                const force = await confirm(apiMessage(e), {title: 'Termín je obsazený', okText: 'Ano, přesto', cancelText: 'Ne'});
                if (force === true) return run(url, {...body, force: true}, successMessage);
                return false;
            }
            notifyError(apiMessage(e));
            return false;
        }
    }, [confirm]);

    if (error) {
        return (
            <div className="space-y-3">
                <BackLink/>
                <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
                    {error?.response?.status === 404 ? 'Požadavek neexistuje nebo byl smazán.' : 'Požadavek se nepodařilo načíst.'}
                </div>
            </div>
        );
    }
    if (!inquiry) return <div className="p-4 text-sm text-gray-500">Načítám…</div>;

    const type = inquiry.type;
    const reservation = inquiry.reservation || {};
    const statusUrl = API_ENDPOINTS.webInquiry.status(inquiry._id);

    // ---------------------------------------------------------------- akce

    const changeStatus = (status, options = {}) => {
        if (!options.modal) return run(statusUrl, {status}, `Stav: ${statusLabel(status, type)}`);
        return openModal(StatusModal, {
            ...options.modal,
            submit: ({message, notify}) => run(statusUrl, {status, message, notify}, options.success || `Stav: ${statusLabel(status, type)}`)
        }, {title: options.title});
    };

    const confirmReservation = () => changeStatus('confirmed', {
        title: `Potvrdit termín ${formatTerm(reservation.date, reservation.time)}`,
        success: 'Termín potvrzen',
        modal: {
            description: <>Termín <strong>{formatTerm(reservation.date, reservation.time, {long: true})}</strong> se zablokuje v kalendáři.</>,
            submitLabel: 'Potvrdit termín',
            notifyLabel: 'Poslat zákazníkovi potvrzení e-mailem',
            messageLabel: 'Doplňující zpráva (nepovinné)',
            messagePlaceholder: 'Např. Náhradní díl máme skladem, oprava bude hotová do hodiny.'
        }
    });

    const cancelReservation = () => changeStatus('cancelled', {
        title: 'Zrušit rezervaci',
        success: 'Rezervace zrušena',
        modal: {
            description: 'Termín se uvolní pro další zákazníky.',
            submitLabel: 'Zrušit rezervaci',
            notifyLabel: 'Informovat zákazníka e-mailem',
            messageLabel: 'Důvod / zpráva pro zákazníka',
            danger: true
        }
    });

    const reschedule = () => openModal(RescheduleModal, {
        inquiry,
        submit: (body) => run(API_ENDPOINTS.webInquiry.reschedule(inquiry._id), body, 'Termín uložen')
    }, {title: 'Změnit termín'});

    const reply = () => openModal(ReplyModal, {
        inquiry,
        offerOption: (type === 'buyback' || type === 'business') && STATUSES_BY_TYPE[type].includes('offer_sent') && inquiry.status !== 'offer_sent',
        defaultSubject: `Re: ${TYPE_MAP[type]?.label} ${inquiry.number}`,
        submit: async ({subject, text, markOffer}) => {
            const ok = await run(API_ENDPOINTS.webInquiry.reply(inquiry._id), {subject, text}, 'E-mail odeslán');
            if (ok && markOffer) await run(statusUrl, {status: 'offer_sent'});
            return ok;
        }
    }, {title: 'Odpovědět e-mailem'});

    /** Zajistí propojení se zákazníkem (vrací aktuální požadavek se zákazníkem nebo null). */
    const ensureCustomer = async () => {
        if (inquiry.customer) return inquiry;
        let updated = null;
        const linked = await openModal(CustomerModal, {
            inquiry,
            submit: async (body) => {
                try {
                    const {data} = await API.post(API_ENDPOINTS.webInquiry.customer(inquiry._id), body, SILENT);
                    updated = data;
                    setInquiry(data);
                    notifySuccess(body.create ? 'Zákazník založen' : 'Zákazník propojen');
                    return true;
                } catch (e) {
                    notifyError(apiMessage(e));
                    return false;
                }
            }
        }, {title: 'Zákazník'});
        return linked === true ? updated : null;
    };

    const createRepair = async () => {
        const current = await ensureCustomer();
        if (!current) return;
        openModal(RepairModal, {
            initialInquiry: current,
            onCreated: async (repair) => {
                if (repair?._id) {
                    await run(API_ENDPOINTS.webInquiry.link(current._id), {repairId: repair._id}, `Zakázka ${repair.repairNumber || ''} vytvořena`);
                }
            }
        }, {title: `Nová zakázka z ${current.number}`});
    };

    const createBuyback = async () => {
        const current = await ensureCustomer();
        if (!current) return;
        navigate(`${routes.admin.buyback.addFull}?inquiryId=${current._id}`);
    };

    const anonymize = async () => {
        const ok = await confirm('Odstranit z požadavku jméno, kontakty a texty zákazníka? Akci nelze vrátit. Použijte jen na žádost zákazníka (GDPR).', {
            title: 'Anonymizovat požadavek', okText: 'Anonymizovat', cancelText: 'Zpět'
        });
        if (ok === true) await run(API_ENDPOINTS.webInquiry.anonymize(inquiry._id), {}, 'Osobní údaje odstraněny');
    };

    const remove = async () => {
        const ok = await confirm(`Smazat požadavek ${inquiry.number}? Zmizí ze seznamu.`, {title: 'Smazat požadavek', okText: 'Smazat', cancelText: 'Zpět'});
        if (ok !== true) return;
        try {
            await API.delete(API_ENDPOINTS.webInquiry.delete(inquiry._id), SILENT);
            notifySuccess('Požadavek smazán');
            refreshInquirySummary();
            navigate(routes.admin.inquiries.full);
        } catch (e) {
            notifyError(apiMessage(e));
        }
    };

    const markUnread = async () => {
        try {
            await API.post(API_ENDPOINTS.webInquiry.markUnread(inquiry._id), {}, SILENT);
            refreshInquirySummary();
            navigate(routes.admin.inquiries.full);
        } catch (e) {
            notifyError(apiMessage(e));
        }
    };

    const actions = getActions({
        inquiry,
        confirmReservation,
        cancelReservation,
        reschedule,
        reply,
        createRepair,
        createBuyback,
        changeStatus
    });

    return (
        <div className="mx-auto max-w-6xl space-y-4">
            <BackLink/>

            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <TypeBadge type={type} short={false}/>
                        <StatusBadge status={inquiry.status} type={type}/>
                        {inquiry.anonymizedAt ? <span className="rounded bg-gray-200 px-2 py-0.5 text-xs">Anonymizováno</span> : null}
                    </div>
                    <h1 className="mt-1 text-xl font-semibold">
                        {inquiry.number} · {inquiry.contact?.company || inquiry.contact?.name}
                    </h1>
                    <p className="text-sm text-gray-500">
                        Přijato {formatDateTime(inquiry.createdAt)}
                        {inquiry.readBy ? ` · otevřel(a) ${userName(inquiry.readBy)}` : ''}
                    </p>
                </div>
                <div className="flex gap-2 text-sm">
                    <button type="button" onClick={markUnread} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 hover:bg-gray-50">
                        Označit jako nepřečtené
                    </button>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="space-y-4">
                    {type === 'reservation' ? <ReservationCard inquiry={inquiry}/> : <RequestCard inquiry={inquiry}/>}
                    <ContactCard inquiry={inquiry}/>
                    <NotesCard inquiry={inquiry} onAdd={(text) => run(API_ENDPOINTS.webInquiry.note(inquiry._id), {text}, 'Poznámka uložena')}/>
                    <HistoryCard inquiry={inquiry}/>
                </div>

                <div className="order-first space-y-4 lg:order-none">
                    <Card title="Co dál">
                        {actions.hint ? <p className="mb-3 text-sm text-gray-600">{actions.hint}</p> : null}
                        <div className="flex flex-col gap-2">
                            {actions.primary.map((action) => (
                                <ActionButton key={action.label} {...action} variant="primary"/>
                            ))}
                            {actions.secondary.map((action) => (
                                <ActionButton key={action.label} {...action}/>
                            ))}
                        </div>
                        <StatusSelect inquiry={inquiry} onChange={(status) => changeStatus(status)}/>
                    </Card>

                    <AssigneeCard
                        inquiry={inquiry}
                        users={users}
                        currentUserId={user?._id}
                        onAssign={(userId) => run(API_ENDPOINTS.webInquiry.assign(inquiry._id), {userId}, userId ? 'Řešitel nastaven' : 'Řešitel odebrán')}
                    />

                    <CustomerCard inquiry={inquiry} onLink={ensureCustomer}/>

                    <Card title="Další akce">
                        <div className="flex flex-col gap-2 text-sm">
                            {inquiry.contact?.email && !inquiry.anonymizedAt ? <ActionButton label="Odpovědět e-mailem" onClick={reply}/> : null}
                            {!inquiry.anonymizedAt ? <ActionButton label="Anonymizovat (GDPR)" onClick={anonymize}/> : null}
                            <ActionButton label="Smazat požadavek" onClick={remove} danger/>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

/** Doporučené akce podle typu a stavu – nejčastější krok je vždy nahoře. */
function getActions({inquiry, confirmReservation, cancelReservation, reschedule, reply, createRepair, createBuyback, changeStatus}) {
    const {type, status} = inquiry;
    const hasEmail = Boolean(inquiry.contact?.email) && !inquiry.anonymizedAt;
    const primary = [];
    const secondary = [];
    let hint = '';

    if (type === 'reservation') {
        if (status === 'new') {
            hint = 'Zákazník čeká na potvrzení termínu. Pokud se termín nehodí, nabídněte jiný.';
            primary.push({label: 'Potvrdit termín', onClick: confirmReservation});
            secondary.push({label: 'Změnit termín', onClick: reschedule});
            secondary.push({label: 'Zrušit rezervaci', onClick: cancelReservation});
            secondary.push({label: 'Spam', onClick: () => changeStatus('spam')});
        } else if (status === 'confirmed') {
            hint = 'Až zákazník zařízení předá, vytvořte zakázku – předvyplní se zařízení, opravy i ceny.';
            primary.push({label: 'Převzato → vytvořit zakázku', onClick: createRepair});
            secondary.push({label: 'Změnit termín', onClick: reschedule});
            secondary.push({label: 'Nedorazil', onClick: () => changeStatus('no_show')});
            secondary.push({label: 'Zrušit rezervaci', onClick: cancelReservation});
        } else if (status === 'converted' && !inquiry.links?.repair) {
            primary.push({label: 'Vytvořit zakázku', onClick: createRepair});
        } else if (['no_show', 'cancelled'].includes(status)) {
            secondary.push({label: 'Nabídnout nový termín', onClick: reschedule});
        }
    }

    if (type === 'contact' || type === 'business') {
        if (!['done', 'spam', 'rejected'].includes(status)) {
            if (hasEmail) primary.push({label: 'Odpovědět e-mailem', onClick: reply});
            if (status === 'new') secondary.push({label: 'Řeším (zavolám)', onClick: () => changeStatus('in_progress')});
            if (type === 'business') secondary.push({label: 'Vytvořit zakázku', onClick: createRepair});
            secondary.push({label: 'Vyřízeno', onClick: () => changeStatus('done')});
            if (status === 'new') secondary.push({label: 'Spam', onClick: () => changeStatus('spam')});
        } else {
            secondary.push({label: 'Znovu otevřít', onClick: () => changeStatus('in_progress')});
        }
    }

    if (type === 'buyback') {
        if (!['converted', 'rejected', 'spam'].includes(status)) {
            hint = status === 'offer_sent'
                ? 'Nabídka odeslána. Když zákazník souhlasí a zařízení přinese, vytvořte výkup.'
                : 'Pošlete zákazníkovi cenovou nabídku e-mailem nebo mu zavolejte.';
            if (hasEmail) (status === 'offer_sent' ? secondary : primary).push({label: 'Poslat nabídku e-mailem', onClick: reply});
            (status === 'offer_sent' ? primary : secondary).push({label: 'Zákazník souhlasí → vytvořit výkup', onClick: createBuyback});
            secondary.push({label: 'Odmítnuto', onClick: () => changeStatus('rejected')});
            if (status === 'new') secondary.push({label: 'Spam', onClick: () => changeStatus('spam')});
        } else if (status === 'converted' && !inquiry.links?.buyback) {
            primary.push({label: 'Vytvořit výkup', onClick: createBuyback});
        } else if (status !== 'converted') {
            secondary.push({label: 'Znovu otevřít', onClick: () => changeStatus('in_progress')});
        }
    }

    if (status === 'spam') hint = 'Označeno jako spam – zákazník nedostal žádný e-mail.';
    return {primary, secondary, hint};
}

// ---------------------------------------------------------------- dílčí komponenty

const BackLink = () => (
    <Link to={routes.admin.inquiries.full} className="inline-block text-sm text-indigo-700 hover:underline">
        ← Zpět na požadavky
    </Link>
);

const Card = ({title, children, aside}) => (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        {title ? (
            <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold">{title}</h2>
                {aside}
            </div>
        ) : null}
        {children}
    </section>
);

const Rows = ({rows}) => (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.filter(([, value]) => value !== undefined && value !== null && value !== '').map(([label, value]) => (
            <React.Fragment key={label}>
                <dt className="text-gray-500">{label}</dt>
                <dd className="whitespace-pre-wrap break-words">{value}</dd>
            </React.Fragment>
        ))}
    </dl>
);

const ActionButton = ({label, onClick, variant, danger}) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full rounded-md px-3 py-2 text-left text-sm font-medium ${
            variant === 'primary'
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : danger ? 'border border-red-200 bg-white text-red-700 hover:bg-red-50' : 'border border-gray-300 bg-white hover:bg-gray-50'
        }`}
    >
        {label}
    </button>
);

const StatusSelect = ({inquiry, onChange}) => (
    <label className="mt-4 block border-t border-gray-100 pt-3 text-xs text-gray-500">
        Ruční změna stavu (bez e-mailu zákazníkovi)
        <select
            value={inquiry.status}
            onChange={(event) => onChange(event.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900"
        >
            {STATUSES_BY_TYPE[inquiry.type].map((status) => (
                <option key={status} value={status}>{statusLabel(status, inquiry.type)}</option>
            ))}
        </select>
    </label>
);

const ReservationCard = ({inquiry}) => {
    const reservation = inquiry.reservation || {};
    const devices = reservationDevices(reservation);
    const anyRepairs = devices.some((item) => item.repairs?.length);
    return (
        <Card title="Rezervace">
            <div className="mb-4 flex flex-wrap items-baseline gap-x-3 rounded-md bg-gray-50 p-3">
                <span className="text-lg font-semibold">{formatTerm(reservation.date, reservation.time, {long: true, year: true})}</span>
                <span className="text-sm text-gray-600">{relativeDay(reservation.date)}</span>
                <span className="text-sm text-gray-600">· {reservation.handover === 'kuryr' ? 'svoz kurýrem' : 'osobně na provozovně'}</span>
            </div>
            {devices.map((item, index) => (
                <ReservationDevice
                    key={index}
                    item={item}
                    title={devices.length > 1 ? `Zařízení ${index + 1}` : 'Zařízení'}
                    separated={index > 0}
                />
            ))}
            <div className={devices.length > 1 ? 'mt-4 border-t border-gray-200 pt-3' : 'mt-4'}>
                <Rows rows={[['Poznámka zákazníka', inquiry.note]]}/>
            </div>
            {anyRepairs ? (
                <div className="mt-3 flex justify-between text-sm font-medium">
                    <span>Celkem{devices.length > 1 ? ` (${devices.length} zařízení)` : ''}</span>
                    <span>
                        <span className="mr-6 text-gray-600">{formatMinutes(reservation.minutes)}</span>
                        {reservation.totalPrice ? formatPrice(reservation.totalPrice) : '-'}
                    </span>
                </div>
            ) : (
                <p className="mt-3 text-sm text-gray-600">Zákazník nevybral konkrétní opravu – počítá se s výchozí náročností {formatMinutes(reservation.minutes)}.</p>
            )}
        </Card>
    );
};

const ReservationDevice = ({item, title, separated}) => {
    const name = reservationDeviceName(item);
    const repairs = item.repairs || [];
    return (
        <div className={separated ? 'mt-4 border-t border-gray-200 pt-4' : ''}>
            <Rows rows={[
                [title, [name, item.familyName && !name.includes(item.familyName) ? `(${item.familyName})` : ''].filter(Boolean).join(' ')],
                ['Vlastní model', item.modelCustom && item.modelCustom !== name ? item.modelCustom : ''],
                ['Popis závady', item.issue]
            ]}/>
            {repairs.length ? (
                <table className="mt-3 w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                            <th className="py-1.5 font-normal">Oprava (cena z webu)</th>
                            <th className="py-1.5 text-right font-normal">Náročnost</th>
                            <th className="py-1.5 text-right font-normal">Cena</th>
                        </tr>
                    </thead>
                    <tbody>
                        {repairs.map((repair, index) => (
                            <tr key={`${repair.repairCatalog || repair.name}-${index}`} className="border-b border-gray-100">
                                <td className="py-1.5">{repair.name}{!repair.repairCatalog ? <span className="ml-1 text-xs text-amber-700">(už není v ceníku)</span> : null}</td>
                                <td className="py-1.5 text-right text-gray-600">{repair.minutes ? formatMinutes(repair.minutes) : '-'}</td>
                                <td className="py-1.5 text-right">{formatPrice(repair.price)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}
        </div>
    );
};

const RequestCard = ({inquiry}) => {
    const rows = [];
    if (inquiry.type === 'buyback') {
        rows.push(
            ['Zařízení', inquiry.buyback?.device],
            ['Stav', CONDITION_LABELS[inquiry.buyback?.condition] || inquiry.buyback?.condition],
            ['Úložiště', inquiry.buyback?.storage],
            ['Příslušenství', inquiry.buyback?.accessories]
        );
    }
    if (inquiry.type === 'business') rows.push(['Zařízení', inquiry.business?.devices]);
    if (inquiry.type === 'contact') rows.push(['Předmět', inquiry.subject]);
    rows.push(['Zpráva', inquiry.message], ['Poznámka', inquiry.note]);
    return (
        <Card title={TYPE_MAP[inquiry.type]?.label}>
            <Rows rows={rows}/>
        </Card>
    );
};

const ContactCard = ({inquiry}) => {
    const contact = inquiry.contact || {};
    return (
        <Card title="Kontakt">
            <Rows rows={[
                ['Firma', contact.company ? `${contact.company}${contact.ico ? `, IČO ${contact.ico}` : ''}` : ''],
                ['Jméno', contact.name],
                ['Telefon', contact.phone ? <a className="font-medium text-indigo-700 hover:underline" href={telHref(contact.phone)}>{contact.phone}</a> : ''],
                ['E-mail', contact.email ? <a className="text-indigo-700 hover:underline" href={`mailto:${contact.email}?subject=${encodeURIComponent(`Re: ${inquiry.number}`)}`}>{contact.email}</a> : ''],
                ['Souhlas', inquiry.consent?.acceptedAt ? `${formatDateTime(inquiry.consent.acceptedAt)}${inquiry.consent.text ? ` – ${inquiry.consent.text}` : ''}` : '']
            ]}/>
        </Card>
    );
};

const AssigneeCard = ({inquiry, users, currentUserId, onAssign}) => {
    const assigned = inquiry.assignedTo;
    const isMine = assigned && String(assigned._id) === String(currentUserId);
    return (
        <Card title="Kdo to řeší">
            <div className="flex flex-col gap-2 text-sm">
                {assigned ? (
                    <p>
                        <strong>{userName(assigned)}</strong>
                        {inquiry.assignedAt ? <span className="text-gray-500"> · od {formatDateTime(inquiry.assignedAt)}</span> : null}
                    </p>
                ) : (
                    <p className="text-gray-600">Zatím nikdo.</p>
                )}
                {!isMine ? <ActionButton label="Převzít – řeším já" onClick={() => onAssign(currentUserId)}/> : null}
                <select
                    value={assigned?._id || ''}
                    onChange={(event) => onAssign(event.target.value || null)}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                    aria-label="Předat požadavek"
                >
                    <option value="">– nikdo –</option>
                    {users.map((item) => <option key={item._id} value={item._id}>{userName(item)}</option>)}
                </select>
            </div>
        </Card>
    );
};

const CustomerCard = ({inquiry, onLink}) => {
    const customer = inquiry.customer;
    return (
        <Card title="Zákazník v ERP">
            {customer ? (
                <div className="space-y-1 text-sm">
                    <div className="font-medium">{customerName(customer) || 'Bez jména'}</div>
                    <div className="text-gray-500">{[customer.emails?.[0]?.value, customer.phones?.[0]?.value].filter(Boolean).join(' · ')}</div>
                    <Link to={routes.admin.customerFull} className="text-xs text-indigo-700 hover:underline">Seznam zákazníků</Link>
                </div>
            ) : (
                <div className="space-y-2 text-sm">
                    <p className="text-gray-600">Zatím nepropojeno. Při vytvoření zakázky nebo výkupu se zákazník dohledá podle e-mailu a telefonu.</p>
                    {!inquiry.anonymizedAt ? <ActionButton label="Najít / založit zákazníka" onClick={onLink}/> : null}
                </div>
            )}
            {inquiry.links?.repair || inquiry.links?.buyback ? (
                <div className="mt-3 border-t border-gray-100 pt-3 text-sm">
                    {inquiry.links?.repair ? (
                        <Link className="font-medium text-emerald-700 hover:underline" to={routes.admin.getEditRepair(inquiry.links.repair._id || inquiry.links.repair)}>
                            Zakázka {inquiry.links.repair.repairNumber || ''} →
                        </Link>
                    ) : null}
                    {inquiry.links?.buyback ? (
                        <Link className="block font-medium text-emerald-700 hover:underline" to={routes.admin.getEditBuyback(inquiry.links.buyback._id || inquiry.links.buyback)}>
                            Výkup {inquiry.links.buyback.buybackNumber || ''} →
                        </Link>
                    ) : null}
                </div>
            ) : null}
        </Card>
    );
};

const NotesCard = ({inquiry, onAdd}) => {
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const submit = async (event) => {
        event.preventDefault();
        if (!text.trim()) return;
        setSaving(true);
        const ok = await onAdd(text.trim());
        setSaving(false);
        if (ok) setText('');
    };
    return (
        <Card title="Interní poznámky">
            <form onSubmit={submit} className="space-y-2">
                <TextArea value={text} onChange={setText} rows={2} placeholder="Např. volal jsem, ozve se zítra…"/>
                <div className="flex justify-end">
                    <button type="submit" disabled={saving || !text.trim()} className="rounded-md bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-900 disabled:opacity-40">
                        Přidat poznámku
                    </button>
                </div>
            </form>
            {inquiry.noteList?.length ? (
                <ul className="mt-3 space-y-2">
                    {[...inquiry.noteList].reverse().map((note) => (
                        <li key={note._id} className="rounded-md bg-yellow-50 p-2 text-sm">
                            <div className="whitespace-pre-wrap">{note.text}</div>
                            <div className="mt-1 text-xs text-gray-500">{userName(note.author)} · {formatDateTime(note.createdAt)}</div>
                        </li>
                    ))}
                </ul>
            ) : null}
        </Card>
    );
};

const HISTORY_LABELS = {
    created: 'Přijato z webu',
    status: 'Změna stavu',
    reschedule: 'Změna termínu',
    email: 'E-mail',
    customer: 'Zákazník',
    assign: 'Řešitel',
    link: 'Propojení',
    anonymize: 'Anonymizace'
};

const RESULT_LABELS = {queued: 've frontě', sent: 'odesláno', failed: 'chyba', skipped: 'neodesláno'};
// skutečný stav e-mailu ve frontě (history.emailId)
const QUEUE_LABELS = {pending: 'čeká na odeslání', sending: 'odesílá se', sent: 'odesláno', failed: 'NEODESLÁNO', expired: 'neodesláno (prošlé)', cancelled: 'zrušeno'};

function emailState(entry) {
    const queued = entry.emailId && typeof entry.emailId === 'object' ? entry.emailId : null;
    if (queued?.status) {
        const label = QUEUE_LABELS[queued.status] || queued.status;
        if (queued.status === 'pending' && queued.attempts) return {label: `${label}, pokus ${queued.attempts} selhal`, failed: true};
        return {label, failed: ['failed', 'expired'].includes(queued.status)};
    }
    return {label: RESULT_LABELS[entry.result] || entry.result, failed: entry.result === 'failed'};
}

function historyText(entry, type) {
    if (entry.action === 'status') return `${statusLabel(entry.from, type)} → ${statusLabel(entry.to, type)}`;
    if (entry.action === 'reschedule') {
        const [fromDate, fromTime] = String(entry.from || '').split(' ');
        const [toDate, toTime] = String(entry.to || '').split(' ');
        return `${formatTerm(fromDate, fromTime)} → ${formatTerm(toDate, toTime)}`;
    }
    if (entry.action === 'email') {
        const [subject, ...rest] = String(entry.text || '').split('\n');
        return {
            title: `${subject}${entry.recipient ? ` → ${entry.recipient}` : ''} (${emailState(entry).label})`,
            body: rest.join('\n').trim()
        };
    }
    return entry.text || '';
}

const HistoryCard = ({inquiry}) => (
    <Card title="Historie">
        <ol className="relative space-y-3 border-l border-gray-200 pl-4">
            {[...(inquiry.history || [])].reverse().map((entry, index) => {
                const text = historyText(entry, inquiry.type);
                const failed = entry.action === 'email' ? emailState(entry).failed : entry.result === 'failed';
                return (
                    <li key={index} className="text-sm">
                        <span className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white ${failed ? 'bg-red-500' : 'bg-gray-300'}`}/>
                        <div className="flex flex-wrap gap-x-2">
                            <span className="font-medium">{HISTORY_LABELS[entry.action] || entry.action}</span>
                            <span className="text-xs text-gray-500">{formatDateTime(entry.createdAt)}{entry.user ? ` · ${userName(entry.user)}` : ''}</span>
                        </div>
                        {typeof text === 'string' ? (
                            text ? <div className="text-gray-700">{text}</div> : null
                        ) : (
                            <>
                                <div className={failed ? 'text-red-700' : 'text-gray-700'}>
                                    {text.title}
                                    {failed ? <> · <Link to={routes.admin.settings.emailsFull} className="underline">fronta e-mailů</Link></> : null}
                                </div>
                                {text.body ? <details className="text-xs text-gray-600"><summary className="cursor-pointer">Text e-mailu</summary><div className="whitespace-pre-wrap pt-1">{text.body}</div></details> : null}
                            </>
                        )}
                        {['status', 'reschedule'].includes(entry.action) && entry.text ? <div className="text-xs text-gray-600">„{entry.text}“</div> : null}
                    </li>
                );
            })}
        </ol>
    </Card>
);

export default InquiryDetail;
